import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SwiperComponent, SwiperConfigInterface } from 'ngx-swiper-wrapper';
import { Subscription, asyncScheduler, fromEvent } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ProcessCard, SOTBasketProductViewModel, SOTBasketViewModel, SOTItemGroupViewModel, SOTProductExtraGroupViewModel, SOTProductExtraItemViewModel, SOTProductTagViewModel, SOTProductViewModel } from '../../models';
import { SOTAdvertisementViewModel } from '../../models/advertisement.model';
import { CouponRedeemResult } from '../../services/ISOTService';
import { SOTDialogResult, SOTService, VoucherActionType } from '../../services';
import { ISOTProductViewModel } from '../../models/product.facade';
import { BaseUnit } from '../../../core/shared/models';
import Utils from '../../../core/shared/utils';
import { env } from '../../../../environments/environment';

@Component({
  selector: "sot-menu",
  templateUrl: "./menu.component.html",
  styleUrls: ["./menu.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuComponent implements OnInit, AfterViewInit, OnDestroy {
  /** artikel container zum scrollen */
  @ViewChild("product_list") product_list_div: ElementRef;
  @ViewChild("basket_product_list") basket_product_list_div: ElementRef;
  @ViewChild("NavigationNextButton") navigationNextBtn: ElementRef;
  @ViewChild("NavigationPrevButton") navigationPrevBtn: ElementRef;
  @ViewChild("NavigationSwiper") navigationSwiper: SwiperComponent;
  @ViewChild("AdvertisementSwiper") advertisementSwiper: SwiperComponent;

  /** inital scroll position */
  private _init_scroll_position: number;

  public adv_swiper_config: SwiperConfigInterface = {
    direction: "horizontal",
    slidesPerView: 1,
    // slidesPerColumn: 1,
    spaceBetween: 0,
    // centeredSlides: false,
    // initialSlide: 0,
    loop: true,
    scrollbar: false,
    navigation: false,
    pagination: false,
    allowTouchMove: true,
    autoplay: {
      delay: 5000,
      disableOnInteraction: true
    }
  };

  public SOTAdvertisementViewModels: SOTAdvertisementViewModel[];
  public SOTItemGroupViewModels: SOTItemGroupViewModel[];
  public SOTBasketViewModel: SOTBasketViewModel;
  public SOTProductTagViewModel: SOTProductTagViewModel[];

  /** zeigt hinweis das man nach unten scrollen kann */
  public show_product_list_bottom_scroll: boolean;
  public show_basket_product_list_bottom_scroll: boolean;

  private _basketChangedSubscription: Subscription;
  private _localeChangedSubscribtion: Subscription;
  private _productScrollSubscription: Subscription;
  private _productListResizeObserver: any;
  private _basketProductScrollSubscription: Subscription;
  private _basketProductResizeObserver: any; // ResizeObserver
  public activeItemGroup: SOTItemGroupViewModel;

  public processCard: null | ProcessCard;
  public get cardUsedAmount(): number {
    return this._sotService.cardTotalAmount;
  }

  public isItemTagEnabled: boolean = false;
  public isItemtagForced: boolean = false;
  public forceTagFilterClosed: boolean = false;
  public selectedTags: number[];
  public isModeOne: boolean = false;
  public isModeTwo: boolean = false;

  public constructor(private _changeDetectorRef: ChangeDetectorRef
    , private _sotService: SOTService) {
    console.debug("--- sot-menu constructor");
  }

  public ngOnInit(): void {
    console.debug("--- sot-menu ngOnInit")
    const me = this;
    me._sotService.storeInitialColorsAndFontSizes();
    const params = me._sotService.GetAndClearMenuComponentBuffer();
    if (params) {
      me._init_scroll_position = params.scroll_position;
    }
    me.isModeOne = me._sotService.useModeOne();
    me.isModeTwo = me._sotService.useModeTwo();
    me.show_product_list_bottom_scroll = false;
    me.show_basket_product_list_bottom_scroll = false;
    me.SOTAdvertisementViewModels = me._sotService.GetAdvertisments();
    me.SOTItemGroupViewModels = me._sotService.GetItemGroups();
    me.SOTBasketViewModel = me._sotService.GetBasket();
    me.itemTagSettings();
    me.selectedTags = me._sotService.GetProductTagFilter();
    // if langugae changes, gets new advertisment
    me._localeChangedSubscribtion = me._sotService.LocaleChanged.subscribe(() => {
      me.SOTAdvertisementViewModels = me._sotService.GetAdvertisments();
      me._rebuildAdvertismentLoop();
      me.PushChanges();
    });

    me._basketChangedSubscription = me._sotService.GetBasket().BasketChanged.subscribe(() => {
      me.PushChanges();
    })

    me.processCard = me._sotService.currentCard;
    me.PushChanges();
  }

  public ngAfterViewInit(): void {
    console.debug("--- sot-menu ngAfterViewInit")
    const me = this;
    me._rebuildAdvertismentLoop();
    // after view init, da dann die höhe bekannt ist
    const product_list_el = me.product_list_div.nativeElement;
    if (product_list_el.scrollHeight > product_list_el.offsetHeight) {
      me.show_product_list_bottom_scroll = true; // show bottom scroll initial
      me.activeItemGroup = me._determineFirstVisibleItemGroup();
      me.PushChanges();

      // Subscribe to a throttled scrollEvent from ProductList
      me._productScrollSubscription = fromEvent(me.product_list_div.nativeElement, "scroll").pipe(
        throttleTime(50, asyncScheduler, { leading: true, trailing: true }),
      ).subscribe(() => {
        // toggles show_product_list_bottom_scroll when scrolled to the bottom or not
        const at_bottom = (product_list_el.scrollTop + product_list_el.offsetHeight) >= (product_list_el.scrollHeight - 5 /*5=bottom_offset*/);
        if (at_bottom === me.show_product_list_bottom_scroll) {
          me.show_product_list_bottom_scroll = !at_bottom;
          me.PushChanges();
        }
        me._updateActiveGroup(at_bottom);
      });
    }

    // after view init, da dann die höhe bekannt ist
    const basket_product_list_el = me.basket_product_list_div.nativeElement;
    if (basket_product_list_el.scrollHeight > basket_product_list_el.offsetHeight) {
      me.show_basket_product_list_bottom_scroll = true; // show bottom scroll initial
      me.PushChanges();
    }
    const checkBasketProductListScroll = () => {
      // toggles show_basket_product_list_bottom_scroll when scrolled to the bottom or not
      const at_bottom = (basket_product_list_el.scrollTop + basket_product_list_el.offsetHeight) >= (basket_product_list_el.scrollHeight - 5 /*5=bottom_offset*/);
      if (at_bottom === me.show_basket_product_list_bottom_scroll) {
        me.show_basket_product_list_bottom_scroll = !at_bottom;
        me.PushChanges();
      }
    };
    // immer binden, da der content dynamisch ist
    // Subscribe to a throttled scrollEvent from BasketProductList
    me._basketProductScrollSubscription = fromEvent(me.basket_product_list_div.nativeElement, "scroll").pipe(
      throttleTime(50, asyncScheduler, { leading: true, trailing: true }),
    ).subscribe(() => {
      checkBasketProductListScroll();
    });

    // start observing for resize basket_product_list_el
    me._basketProductResizeObserver = new (window as any).ResizeObserver(() => { checkBasketProductListScroll(); });
    me._basketProductResizeObserver.observe(basket_product_list_el);

    // initial scroll if needs
    if (me._init_scroll_position > 0) {
      me.scroll_position = me._init_scroll_position;
      me.PushChanges();
    }

    me.openFilterTagDialiogForced();
  }

  public ngOnDestroy(): void {
    console.debug("--- sot-menu ngOnDestroy");
    const me = this;
    if (me._localeChangedSubscribtion) {
      me._localeChangedSubscribtion.unsubscribe();
    }
    if (me._basketChangedSubscription) {
      me._basketChangedSubscription.unsubscribe();
    }
    if (me._productScrollSubscription) {
      me._productScrollSubscription.unsubscribe();
    }
    if (me._basketProductScrollSubscription) {
      me._basketProductScrollSubscription.unsubscribe();
    }
    if (me._basketProductResizeObserver) {
      me._basketProductResizeObserver.unobserve(me.basket_product_list_div.nativeElement);
    }
    if (me._productListResizeObserver) {
      me._productListResizeObserver.unobserve(me.product_list_div.nativeElement);
    }
  }

  /**
   * Calculates the first visible item group based on the scroll position and an offset leniency.
   * Iterates through the item groups and finds the one that is within the visible threshold.
   */
  private _determineFirstVisibleItemGroup(): SOTItemGroupViewModel {
    const me = this;
    const listEl = me.product_list_div.nativeElement;
    const PX_OFFSET_LENIENCY = 200; // Used to cause an ItemGroup to be active before it reaches to the top
    const thresholdTop = listEl.scrollTop + PX_OFFSET_LENIENCY;
    let lastOffsetTop = 0;
    let result = null;
    for (const itemgroup of me.SOTItemGroupViewModels) {
      const itemgroupOffset = document.getElementById("itemgroup" + itemgroup.id).offsetTop;
      if (itemgroupOffset >= lastOffsetTop && itemgroupOffset <= thresholdTop) {
        result = itemgroup;
        lastOffsetTop = itemgroupOffset;
      }
    }
    return result;
  }

  /**
 * Sets the active item group based on visibility and position.
 * @param  atBottom - If true, sets the active group to the last group in the list.
 */
  private _updateActiveGroup(atBottom: boolean) {
    const me = this;
    if (atBottom) {
      const lastGroup = me.SOTItemGroupViewModels[me.SOTItemGroupViewModels.length - 1];
      const lastGroupActive = lastGroup && lastGroup !== me.activeItemGroup;
      if (lastGroupActive) {
        me.activeItemGroup = lastGroup;
      }
    } else {
      const nextVisibleGroup = me._determineFirstVisibleItemGroup();
      if (me.activeItemGroup !== nextVisibleGroup) {
        me.activeItemGroup = nextVisibleGroup;
      }
    }
    me.PushChanges();
  }

  /**
 * Handles the activation and scrolling to a specific item group.
 * @param itemGroup - The item group to activate and scroll to.
 */
  public getActiveGroupAndScroll(itemGroup: SOTItemGroupViewModel, event: Event) {
    const me = this;
    event.preventDefault();
    event.stopPropagation();

    const container = me.product_list_div && me.product_list_div.nativeElement;
    const target = document.getElementById("itemgroup" + itemGroup.id);

    if (container && target) {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const scrollTop = container.scrollTop + (targetRect.top - containerRect.top) - 18; // 18px offset for header to prevent glitchy scrolling
      const duration = 300; // Duration of the scroll animation in milliseconds
      const start = container.scrollTop;
      const change = scrollTop - start;
      let startTime: number = null;

      // Arrow-Function übernimmt das this von außen (me)
      const animateScroll = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = timestamp - startTime;
        const val = Math.min(progress / duration, 1);
        container.scrollTop = start + change * val;
        if (progress < duration) {
          window.requestAnimationFrame(animateScroll);
        } else {
          container.scrollTop = scrollTop;
          if (me.activeItemGroup !== itemGroup) {
            me.activeItemGroup = itemGroup;
            me.PushChanges();
          }
        }
      };
      window.requestAnimationFrame(animateScroll); // requestAnimationFrame for performance
    } else {
      if (me.activeItemGroup !== itemGroup) {
        me.activeItemGroup = itemGroup;
        me.PushChanges();
      }
    }
  }
  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._sotService.coreLogService.error("MenuComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  public back(): void {
    this._sotService.OpenOrderCancelDialog(false);
  }

  public get hasAdvertisement(): boolean {
    return this.SOTAdvertisementViewModels.length > 0;
  }

  public get logo_image_path(): string {
    return this._sotService.GetSettings().logo_image_path;
  }

  public get navigationSliderconfig(): SwiperConfigInterface {
    return {
      init: true,
      direction: "vertical",
      slidesPerView: "auto",
      slidesPerColumn: 1,
      centerInsufficientSlides: false, // dont center the slides if not enough
      keyboard: false,
      mousewheel: false,
      scrollbar: false,
      pagination: false,
      navigation: {
        nextEl: this.navigationNextBtn.nativeElement,
        prevEl: this.navigationPrevBtn.nativeElement,
      },
      autoplay: false,
      watchOverflow: true, // Disable navigation if there are not enough slides
    };
  }

  public navigationByAdvertisement(adv: SOTAdvertisementViewModel): void {
    const me = this;
    if (adv.model.item_group_id > 0) {
      const item_group = me.SOTItemGroupViewModels.find((item_group) => item_group.id === adv.model.item_group_id);
      if (item_group) {
        me.scrollToItemGroup(item_group);
      } else {
        me._sotService.coreLogService.error("MenuComponent.navigationByAdvertisement", "adv item_group_id not found in item groups", adv.model.item_group_id);
      }
    }
  }

  /** ermittelt die actuelle scroll position in der bestellübersicht */
  private get scroll_position(): number {
    try {
      return this.product_list_div.nativeElement.scrollTop;
    } catch (error) {
      this._sotService.coreLogService.error("MenuComponent.scroll_position get", "try catch", error);
      return 0;
    }
  }
  /** zeigt die Artikelgruppe als Bild an */
  public get hideItemGroupLabel(): boolean {
    const me = this
    return me._sotService.GetSettings().use_itemgroup_as_image;
  }

  /** setzt die scroll position in der bestellübersicht */
  private set scroll_position(position: number) {
    try {
      this.product_list_div.nativeElement.scrollTop = position;
    } catch (error) {
      this._sotService.coreLogService.error("MenuComponent.scroll_position set", "try catch", error);
    }
  }

  /** langkey, ob pfand incl. oder zzgl. */
  public get deposit_langkey(): string {
    return this._sotService.deposit_view ? "details_incl_deposit" : "details_plus_deposit";
  }

  /** use e-commerce */
  public get use_eCommerce(): boolean {
    const me = this;
    return me._sotService.GetSettings().use_eCommerce;
  }

  /** has product base unit*/
  public hasBaseUnit(productBaseUnit: BaseUnit) {
    const me = this;
    return productBaseUnit !== BaseUnit.NONE;
  }

  /** Product price per unit */
  public pricePerUnit(basePrice: number, baseFactor: number): string {
    const me = this;
    const pricePerUnit = Utils.pricePerUnit(basePrice, baseFactor);
    return me.priceFormatter(pricePerUnit);
  }

  private _baseUnit = BaseUnit;
  /**baseUnit */
  public get baseUnit() {
    const me = this;
    return me._baseUnit;
  }


  // format price base on language
  public priceFormatter(price: number): string {
    const me = this;
    return me._sotService.formatCurrency(price);
  }

  /**Alcohol content */
  public product_alcoholContent(alcoholContent: number): string {
    const me = this;
    return alcoholContent.toString();
  }

  /** Caffeine content */
  public product_caffeineContent(caffeineContent: number): string {
    const me = this;
    return caffeineContent.toString();
  }

  public get useCoupon(): boolean {
    return this._sotService.GetSettings().use_coupons;
  }

  public get canDisplayRedeemBenefitBtn() {
    return this._sotService.canDisplayRedeemBenefitBtn;
  }

  public async AddCoupon(): Promise<void> {
    const me = this;
    const scroll_position = me.scroll_position; // calc and save it

    if (me._sotService.GetSettings().scan_coupons) {
      me._sotService.GoToCouponScan();
    } else {
      // _RetryAddCoupon läst den User den Code immer wieder eingeben
      me._RetryAddCoupon(scroll_position);
    }
  }

  /** hält den Anwender in einer Schleife zum manuellen Eingeben von einem Coupon Code, bis er diese eingabe abbricht */
  private async _RetryAddCoupon(scroll_position: number): Promise<void> {
    const me = this;
    const couponCode = await me._sotService.OpenManualCouponInput();
    if (couponCode !== "cancel") {
      if (couponCode) {
        const state = await me._sotService.openCouponProduct(couponCode);
        if (state === CouponRedeemResult.Interrupted) {
          // SOT was reset while waiting for the POS — nothing left to do
          return;
        }
        if (state === CouponRedeemResult.Done) {
          me._sotService.GoToMenu(null, scroll_position);
        } else if (state === CouponRedeemResult.Cancelled) {
          // user backed out of the product screen — return to manual input loop
          me._RetryAddCoupon(scroll_position);
        } else if (state === CouponRedeemResult.CodeNotValid) {
          me._sotService.OpenCouponCouldNotBeReadDialog(async (retry) => {
            if (retry) {
              me._RetryAddCoupon(scroll_position); // no await, keep running
            } else {
              me._sotService.GoToMenu(null, scroll_position);
            }
          });
        } else if (state === CouponRedeemResult.ProductNotAvailable) {
          me._sotService.openCouponArticleNotAvailable(async (retry) => {
            if (retry) {
              me._RetryAddCoupon(scroll_position);
            } else {
              me._sotService.GoToMenu(null, scroll_position);
            }
          });
        } else if (state === CouponRedeemResult.AlreadyInCart) {
          me._sotService.openCouponInCartDialog((goToBasket) => {
            if (goToBasket) {
              me._sotService.GoToCheckout();
            } else {
              me._RetryAddCoupon(scroll_position);
            }
          });
        } else {
          me._sotService.GoToMenu(null, scroll_position);
        }
      } else {
        me._sotService.GoToMenu(null, scroll_position);
      }
    } // else ignore, abgebrochen durch anwendung
  }


  public async openCombinedChoiceDialog() {
    const me = this;
    const result = await me._sotService.openCombinedChoiceDialog();
    if (result === SOTDialogResult.ScanCoupon) {
      me.AddCoupon();
    } else if (result === SOTDialogResult.RedeemBenefit) {
      me.openScanVoucherToRedeemBenefit();
    } else { //result === SOTDialogResult.Back
      //  do nothing, dialog will be closed
    }
  }

  public async openScanVoucherToRedeemBenefit() {
    const me = this;
    me._sotService.AddVoucher(VoucherActionType.RedeemBenefit, false);
  }

  /**
   * Navigates to a products page if it is available
   * @param product ProductViewModel of the product
   */
  public openProduct(product: ISOTProductViewModel, itemGroup: SOTItemGroupViewModel): void {
    const me = this;
    if (product.is_available && product.schedule.active) {
      const scroll_position = me.scroll_position; // calc and save it
      me._sotService.OpenProduct(product, itemGroup, null, () => {
        me._sotService.GoToMenu(null, scroll_position);
      });
    }
  }

  public editProduct(product: SOTBasketProductViewModel): void {
    const me = this;
    const scroll_position = me.scroll_position; // calc and save it
    const productEdit = me._sotService.GetBasket().getProductForEdit(product);
    me._sotService.OpenEditBasketProduct(productEdit, () => {
      me._sotService.GoToMenu(null, scroll_position);
    });
  }

  public get is_basket_empty(): boolean {
    return this._sotService.GetBasket().getCount() === 0;
  }

  public goToCheckout() {
    this._sotService.GoToCheckout();
  }

  public openCancelDialog() {
    this._sotService.OpenOrderCancelDialog(true);
  }

  public scrollToItemGroup(itemgroup: SOTItemGroupViewModel) {
    document.getElementById("itemgroup" + itemgroup.id).scrollIntoView({
      behavior: "smooth",
    });
  }

  public removeProductFromBasket(product: SOTBasketProductViewModel) {
    this.SOTBasketViewModel.removeBasketProduct(product);
    this.PushChanges();
  }

  public setProductAmount(product: SOTBasketProductViewModel, amount: number) {
    product.count = amount;
    if (product.count <= 0) {
      this.removeProductFromBasket(product);
      this.hideBasketScrollArrow();
    }
  }

  /** product is not recipe linking and has no extras */
  public canAddDirectlytoCart(product: SOTProductViewModel): boolean {
    const me = this;
    let productExtras = product.extraGroups.filter(extragroup => !extragroup.hasNoChoice);
    return !product.is_recipe_linking && productExtras.length === 0;
  }

  /**add product to basket */
  public addToBasket(product: SOTProductViewModel, group: SOTItemGroupViewModel) {
    const me = this;
    const basketProduct = new SOTBasketProductViewModel(product);
    // Prefill Single Option Extragroups
    product.extraGroups.forEach(extragroup => {
      if (extragroup.hasNoChoice && extragroup.extra_items.length > 0) {
        const extra = extragroup.extra_items[0];
        const subextra = (extra.without_subitem && !extra.with_one_subitem) ? null : extra.sub_items[0];
        while (this.getCountOfExtra(basketProduct, extragroup, extra, subextra) < extragroup.max_choices) {
          basketProduct.addExtraGroup(extragroup.query, extra.id, subextra ? subextra.id : 0);
        }
      }
    });
    const hasRequiredAge = product && product.model && product.model.ask_age;
    if (hasRequiredAge) {
      me._sotService.validateAgeRequirement(product).then((result) => {
        if (result) {
          me._sotService.GetBasket().addBasketProduct(basketProduct);
          me.PushChanges();
        }
      })
    } else {
      me._sotService.GetBasket().addBasketProduct(basketProduct);
    }
    if (me.hasTagFilterSelected) {
      group.recommendations.forEach(rec => {
        const hasSomeProductTags = (product_tag_ids: number[]): boolean => {
          return me.selectedTags.every((id => product_tag_ids.indexOf(id) >= 0))
        }
        rec.products = rec.products.filter(product => hasSomeProductTags(product.product_tag_ids));
      });
      group.recommendations = group.recommendations.filter(itemgroup => itemgroup.products.length > 0);
    }

    if (group && group.recommendations.length > 0) {
      me._sotService.OpenProductRecommendationDialog(group, me.isItemTagEnabled, () => { });
    }
  }

  public get basketProductListEl() {
    const me = this;
    return me.basket_product_list_div.nativeElement;
  }

  /** show scroll arrow depending on offsetHeight of basket Product list and scrollHeight */
  public showBasketScrollArrow() {
    const me = this;
    if (me.basketProductListEl.scrollHeight > me.basketProductListEl.offsetHeight) {
      me.show_basket_product_list_bottom_scroll = true;
      me.PushChanges();
    }
  }

  /** hide scroll arrow depending on offsetHeight of basket Product list and scrollHeight */
  public hideBasketScrollArrow() {
    const me = this;
    if (me.basketProductListEl.offsetHeight >= me.basketProductListEl.scrollHeight) {
      me.show_basket_product_list_bottom_scroll = false;
      me.PushChanges();
    }
  }

  public getCountOfExtra(basketProduct: SOTBasketProductViewModel, extragroup: SOTProductExtraGroupViewModel, extra?: SOTProductExtraItemViewModel, sub?: SOTProductExtraItemViewModel) {
    const existingExtraGroup = basketProduct.extra_groups.find(eg => eg.query === extragroup.query);
    if (extra) {
      const existingExtra = existingExtraGroup ? existingExtraGroup.extras.find(e => e.extra_id === extra.id) : null;
      if (sub) {
        const existingSub = existingExtra ? existingExtra.sub_extras.find(e => e.extra_id === sub.id) : null;
        return existingSub ? existingSub.count : 0;
      }
      return existingExtra ? existingExtra.count : 0;
    }
    return existingExtraGroup ? existingExtraGroup.extras.reduce((a, b) => a + b.count, 0) : 0;
  }

  /** Used for resetting the Advertisement Loop,
   * This is currently needing because the slide duplicates don't work with the image_directive
   * TODO keni: Fix underlying Problem or atleast find a better event to attach to then using setTimeout
   */
  private _rebuildAdvertismentLoop() {
    setTimeout(() => {
      if (this.advertisementSwiper) {
        const swiper = this.advertisementSwiper.directiveRef.swiper();
        if (swiper) {
          swiper.loopCreate();
        }
      }
    }, 2000);
  }

  /** item tag settings*/
  public async itemTagSettings() {
    const me = this;
    await me._sotService.getWebshopSettingsDataModel().then((webshopSettings) => {
      if (webshopSettings) {
        me.isItemTagEnabled = webshopSettings.is_itemtag_sot_enabled;// tags am SOT aktiv
        me.isItemtagForced = webshopSettings.is_itemtag_sot_forced;// tags beim start abfragen
        me.PushChanges();
      }
    });
  }

  /** product tags */
  public GetProductTagsById(tagIds: number[]) {
    const me = this;
    let productTags: SOTProductTagViewModel[];
    if (me.hasTagFilterSelected) {
      productTags = me._sotService.GetProductTagsByIds(tagIds).sort((a, b) =>
        me._sotService.displaySelectedTagFirst(a.id, b.id)).slice(0, 5)
    } else {
      productTags = me._sotService.GetProductTagsByIds(tagIds).slice(0, 5);
    }
    return productTags;
  }

  public get hasTagFilterSelected(): boolean {
    const me = this;
    return me.selectedTags.length > 0;
  }

  public get selectedTagFilterCount(): number {
    const me = this;
    return me.selectedTags.length;
  }

  public async openFilterTagDialog() {
    const me = this;
    me.selectedTags = await me._sotService.openProductTagFilterDialog();
    me._sotService.SetProductTagFilter(me.selectedTags);
    me.loadTagFilterItems();
    me.PushChanges();
  }
  // Checks if the accessibility button shuld be shown if the ordertype page is skipped
  public get showAccessabilityButton(): boolean {
    // Der Button wird nur angezeigt, wenn die Ordertype-Seite übersprungen wurde
    return this._sotService.GetSettings().skip_ordertype_if_no_choise && this._sotService.hasSkippedOrdertypePage;
  }
  public async openAccessabiltyDialog(): Promise<void> {
    const me = this;
    try {
      const result = await me._sotService.openAccessabiltyDialog(me.isModeOne, me.isModeTwo);

      if (result) {
        if (result.useModeOne !== undefined) {
          me._sotService.Accessabilty.SetModeOne(result.useModeOne);
          me.isModeOne = result.useModeOne;
        }
        if (result.useModeTwo !== undefined) {
          me._sotService.Accessabilty.SetModeTwo(result.useModeTwo);
          me.isModeTwo = result.useModeTwo;
          me._sotService.updateCssVariables(me.isModeTwo);
        }
        me.PushChanges();
      }
    } catch (error) {
      console.error('Error opening accessibility dialog:', error);
    }
  }
  /**if item tag is forced, filter tag dialog will be displayed
   * avoid to display forced filter tag dialog after close and on navigation back to menu*/
  public openFilterTagDialiogForced() {
    const me = this;
    me.forceTagFilterClosed = me._sotService.getForceTagFilterClosed();
    setTimeout(() => {
      if (me.isItemTagEnabled && me.isItemtagForced && !me.forceTagFilterClosed) {
        me.openFilterTagDialog();
      }
    }, 500);
    me.PushChanges();
  }

  public loadTagFilterItems() {
    const me = this;
    me.SOTItemGroupViewModels = me._sotService.GetItemGroups();
    me.tagFilterItemScroll();
    me.PushChanges();
  }

  /** reset selected filter tag */
  public resetTagFilter() {
    const me = this;
    me.selectedTags = [];
    me._sotService.SetProductTagFilter(me.selectedTags);
    me.loadTagFilterItems();
    me.PushChanges();
  }

  /**
 * Sets up a ResizeObserver to toggle the bottom scroll visibility
 * based on the product list's height and triggers view updates.
 */
  public tagFilterItemScroll() {
    const me = this;
    const product_list_el = me.product_list_div.nativeElement;
    me._productListResizeObserver = new (window as any).ResizeObserver(() => {
      me.show_product_list_bottom_scroll = product_list_el.scrollHeight > product_list_el.offsetHeight;
      me._updateActiveGroup(!me.show_product_list_bottom_scroll);
      me.PushChanges();
    });
    me._productListResizeObserver.observe(me.product_list_div.nativeElement);
  }
  /** Zwischenlösung für die Warengruppenleisten Farben */
  public useAltMenuColors(): boolean {
    return env.HypCusNumber == 26659; // my Indigo;
  }
}